import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Grid,
  Alert,
  Box,
  Chip,
  Typography,
  Tabs,
  Tab,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { Edit as EditIcon, Business as BusinessIcon, WhatsApp as WhatsAppIcon, Warning as WarningIcon } from '@mui/icons-material'
import { smbsApi, SMBData } from '../../services/api'
import { MAX_DOCUMENT_SIZE_LABEL } from '../../constants/uploads'
import { useNotification } from '../../contexts/NotificationContext'
import { WhatsAppStatusCard } from '../whatsapp/WhatsAppStatusCard'
import { WhatsAppCredentialsDialog } from '../whatsapp/WhatsAppCredentialsDialog'
import { GridCloseIcon } from '@mui/x-data-grid'
import { validatePdfFile } from '../../utils/uploads'
import DocumentFilePickerButton from '../DocumentFilePickerButton'
import BusinessDocumentUpload from '../dashboard/BusinessDocumentUpload'

interface EditSMBDialogProps {
  open: boolean
  onClose: () => void
  smb: SMBData
  onSuccess: (smb: SMBData, scrapingPromise?: Promise<any>) => void
}

const businessTypes = [
  { value: 'hospitality', label: 'Hospitality' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'fitness', label: 'Fitness' },
  { value: 'dental', label: 'Dental' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'beauty', label: 'Beauty & Spa' },
  { value: 'legal', label: 'Legal Services' },
  { value: 'retail', label: 'Retail' },
  { value: 'coaching', label: 'Coaching' },
  { value: 'other', label: 'Other' },
]

const industries = [
  { value: 'hotel', label: 'Hotel' },
  { value: 'resort', label: 'Resort' },
  { value: 'clinic', label: 'Medical Clinic' },
  { value: 'hospital', label: 'Hospital' },
  { value: 'gym', label: 'Gym' },
  { value: 'yoga', label: 'Yoga Studio' },
  { value: 'dental_clinic', label: 'Dental Clinic' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'cafe', label: 'Cafe' },
  { value: 'spa', label: 'Spa & Wellness' },
  { value: 'salon', label: 'Beauty Salon' },
  { value: 'law_firm', label: 'Law Firm' },
  { value: 'retail_store', label: 'Retail Store' },
  { value: 'coaching', label: 'Life/Business Coaching' },
  { value: 'other', label: 'Other' },
]

const statusOptions = [
  { value: 'active', label: 'Active', color: 'success' },
  { value: 'trial', label: 'Trial', color: 'warning' },
  { value: 'inactive', label: 'Inactive', color: 'default' },
] as const

const EditSMBDialog: React.FC<EditSMBDialogProps> = ({ open, onClose, smb, onSuccess }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [formData, setFormData] = useState({
    name: '',
    business_type: '',
    industry: '',
    owner_name: '',
    owner_email: '',
    phone: '',
    website: '',
    status: 'active',
  })
  const [documentFile, setDocumentFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [currentTab, setCurrentTab] = useState(0)
  const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false)
  const [nudgeOpen, setNudgeOpen] = useState(false)
  const { showNotification } = useNotification()

  // Populate form when SMB data changes
  useEffect(() => {
    if (smb) {
      setFormData({
        name: smb.name || '',
        business_type: smb.business_type || '',
        industry: smb.industry || '',
        owner_name: smb.owner_name || '',
        owner_email: smb.owner_email || '',
        phone: smb.phone || '',
        website: smb.website || '',
        status: smb.status || 'active',
      })
    }
  }, [smb])

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value

    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    if (error) setError(null) // Clear error when user starts typing
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const validationError = validatePdfFile(file)
    if (validationError) {
      setError(validationError)
      event.target.value = ''
      return
    }

    setDocumentFile(file)
    setError(null)
  }

  const confirmUpdate = (deleteData: boolean) => {
    const isRemoving = (String(formData.website) || '').trim().length === 0
    const payload: any = {
      ...formData,
      smb_id: smb.id,
      delete_scraped_data: deleteData,
      clear_website: isRemoving
    }

    const updatePromise = documentFile
      ? smbsApi.createUpdateSMB(payload, { file: documentFile })
      : smbsApi.createUpdateSMB(payload)
    const optimisticSMB = { ...smb, ...formData, website: isRemoving ? '' : formData.website }
    onSuccess(optimisticSMB, updatePromise)
    if (documentFile) {
      showNotification('Uploading business document…', 'info')
      void updatePromise
        .then(() => {
          showNotification('Business document uploaded successfully!', 'success')
          setDocumentFile(null)
        })
        .catch((err: any) => {
          console.error('Error uploading SMB document:', err)
          showNotification(err?.response?.data?.detail || 'Failed to upload business document.', 'error')
        })
    }

    setNudgeOpen(false)
    onClose()
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()

    // Validate required fields
    if (!formData.name || !formData.business_type || !formData.industry ||
      !formData.owner_name || !formData.owner_email || !formData.phone) {
      setError('Please fill in all required fields')
      return
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.owner_email)) {
      setError('Please enter a valid email address')
      return
    }

    const hadWebsite = (smb.website || '').trim().length > 0
    const isRemoving = (String(formData.website) || '').trim().length === 0

    if (hadWebsite && isRemoving) {
      setNudgeOpen(true)
      return
    }
    confirmUpdate(false)
  }

  const handleClose = () => {
    setError(null)
    onClose()
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { minHeight: isMobile ? 'auto' : '700px' }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon color="primary" />
            Edit Business Details
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <GridCloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Created: {formatDate(smb.created_at)}
        </Typography>
      </DialogTitle>

      {/* Tab Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={currentTab} onChange={(_e, newValue) => setCurrentTab(newValue)}>
          <Tab
            icon={<BusinessIcon />}
            label="Business Info"
            iconPosition="start"
          />
          <Tab
            icon={<WhatsAppIcon />}
            label="WhatsApp Integration"
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {currentTab === 0 && (
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Business Metrics Summary */}
            {smb.metrics && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BusinessIcon fontSize="small" />
                  Performance Overview (Last 30 days)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <Typography variant="h6" color="primary">
                      {smb.metrics.total_leads || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total Leads
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="h6" color="success.main">
                      {smb.metrics.total_bookings || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Bookings
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="h6" color="info.main">
                      {smb.metrics.conversion_rate || 0}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Conversion
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="h6" color="warning.main">
                      ${smb.metrics.revenue_30d || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Revenue
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Business Name"
                  value={formData.name}
                  onChange={handleChange('name')}
                  required
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Status"
                  value={formData.status}
                  onChange={handleChange('status')}
                  required
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={option.label}
                          color={option.color}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Business Type"
                  value={formData.business_type}
                  onChange={handleChange('business_type')}
                  required
                >
                  {businessTypes.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Industry"
                  value={formData.industry}
                  onChange={handleChange('industry')}
                  required
                >
                  {industries.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Owner Name"
                  value={formData.owner_name}
                  onChange={handleChange('owner_name')}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact Email"
                  type="email"
                  value={formData.owner_email}
                  onChange={handleChange('owner_email')}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={formData.phone}
                  onChange={handleChange('phone')}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Website"
                  value={formData.website}
                  onChange={handleChange('website')}
                  placeholder="https://www.business.com"
                />
              </Grid>

              {smb.document_name || smb.document_original_path ? (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Business Document
                  </Typography>
                  <BusinessDocumentUpload smb={smb as any} onFileSelect={setDocumentFile} />
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <DocumentFilePickerButton
                    file={documentFile}
                    onChange={handleFileChange}
                    emptyLabel="📄 Upload Document (Optional)"
                    helperText={`Supported: PDF (Max ${MAX_DOCUMENT_SIZE_LABEL})`}
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>

          <DialogActions sx={{ p: 2, pt: 1 }}>
            <Button
              onClick={handleClose}
              color="inherit"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<EditIcon />}
              sx={{ minWidth: 140 }}
            >
              Update Business
            </Button>
          </DialogActions>
        </form>
      )}

      {/* WhatsApp Integration Tab */}
      {currentTab === 1 && (
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <WhatsAppIcon color="primary" />
              WhatsApp Business API Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Configure WhatsApp Business API credentials to enable automated messaging for this business.
            </Typography>

            <WhatsAppStatusCard
              smbId={smb.id}
              onConfigureCredentials={() => setWhatsappDialogOpen(true)}
            />
          </Box>
        </DialogContent>
      )}

      {/* WhatsApp Credentials Dialog */}
      {smb.id && (
        <WhatsAppCredentialsDialog
          open={whatsappDialogOpen}
          onOpenChange={setWhatsappDialogOpen}
          smbId={smb.id}
          onSuccess={() => {
            setWhatsappDialogOpen(false)
            window.dispatchEvent(new CustomEvent('whatsapp-credentials-updated', { detail: { smbId: smb.id } }))
          }}
        />
      )}
      <Dialog open={nudgeOpen} onClose={() => setNudgeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main' }}>
          <WarningIcon />
          Remove Scraped Data?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            You are removing the website <strong>{smb.website}</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            We still have scraped content (FAQs, business context) stored from this site.
            Do you want to delete this data to clean up storage, or keep it?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: 'column', gap: 1 }}>
          <Button
            variant="contained"
            color="error"
            fullWidth
            onClick={() => confirmUpdate(true)} // User clicked "Delete Data"
          >
            Remove Website & Delete Data
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            fullWidth
            onClick={() => confirmUpdate(false)} // User clicked "Keep Data"
          >
            Remove Website Only (Keep Data)
          </Button>
          <Button
            variant="text"
            onClick={() => setNudgeOpen(false)}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}

export default EditSMBDialog
